import { Box, InputLabel, Typography } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { InputRowType } from '../../../interfaces';
import { getCommissionEmployees } from '../../../helpers/items';
import { roundToMaxDecimalPlaces } from '../../../utils/global/helper';
import { ProductFormValues } from '../../../utils/catalog/productForm';

type CommissionEmployee = ReturnType<typeof getCommissionEmployees>[number];
type Commission = ProductFormValues['employeeCommissions'][number];

/** One rate field per commission-paid employee, built like the Cost row so they share its look. */
export function commissionRateRows(
  employees: CommissionEmployee[],
  commissions: Commission[],
  onRateChange: (employeeId: string, percent: number | undefined) => void,
): InputRowType[] {
  if (!employees.length) return [];

  const rateRows = employees.map((employee): InputRowType => {
    const name = employee.displayName || employee.user?.firstName || 'Unnamed';
    const field = `commissionRate-${employee.employeeId}`;
    const rate = commissions.find(c => c.employeeId === employee.employeeId)
      ?.rate;
    return [
      {
        label: name,
        name: field,
        type: 'node',
        node: (
          <div className="input">
            <InputLabel htmlFor={field}>
              <Typography variant="h5">{name}</Typography>
            </InputLabel>
            <NumericFormat
              id={field}
              placeholder="Enter commission"
              allowNegative={false}
              decimalScale={2}
              suffix="%"
              isAllowed={({ floatValue }) =>
                floatValue === undefined || floatValue <= 100
              }
              value={
                rate === undefined ? '' : roundToMaxDecimalPlaces(rate * 100, 2)
              }
              onValueChange={({ floatValue }) =>
                onRateChange(employee.employeeId, floatValue)
              }
            />
          </div>
        ),
      },
    ];
  });

  return [
    [
      {
        label: 'Commission per team member',
        name: 'commissionRates',
        type: 'node',
        node: (
          <div className="input">
            <Box
              sx={{
                flex: 1,
                px: 3,
                py: 1.5,
                bgcolor: theme => theme.palette.lightGrey.main,
              }}>
              <Typography variant="h5">Commission per team member</Typography>
              <Typography variant="caption" color="text.secondary">
                Applies to every variant. Only team members paid on commission
                are listed.
              </Typography>
            </Box>
          </div>
        ),
      },
    ],
    ...rateRows,
  ];
}
